import { api } from './queryInterceptors';

import type { AxiosResponse } from 'axios';
import type { Demographic} from '@/types/respondent';
import type { PaginatedResponse } from '@/types/api';

/**
 * Retrieve a respondent's non-deleted and non-ignored demographic replies from the API.
 *
 * @param respondentId - The ID of the respondent whose demographics are being fetched.
 * @returns A Promise resolving to the Axios response containing the paginated list of demographics.
 */
export async function demographicsGetQuery(
  respondentId: string,
): Promise<AxiosResponse<PaginatedResponse<Demographic>>> {
  const requestOptions = {
    method: 'GET',
    url: `/demographics/`,
    params: {
      deletedAtIsNull: true,
      ignoredAtIsNull: true,
      respondent: respondentId
    }
  };
  return api(requestOptions);
}

/**
 * Post a list of demographics to the database.
 *
 * @param demographicsToPost - The list of demographics to be saved via the API.
 * @returns A Promise resolving to the Axios response containing the list of saved demographics.
 */
export async function demographicsPostQuery(
  demographicsToPost: Demographic[]
): Promise<AxiosResponse<Demographic[]>> {
  const requestOptions = {
    method: 'POST',
    url: `/demographics/`,
    data: demographicsToPost,
  };
  return api(requestOptions);
}

/**
 * Post a single demographic to the database. When posting a file, only one demographic can be posted at a time.
 *
 * @param demographic - The demographic to save, sent as a FormData object.
 * @returns A Promise resolving to the Axios response containing the saved demographic.
 */
export async function demographicFilePostQuery(
  demographic: Demographic,
): Promise<AxiosResponse<Demographic>> {
  const formData: FormData = new FormData();

  formData.append('id', demographic.id);
  formData.append('createdAt', demographic.createdAt);
  formData.append('field', demographic.field);
  if (demographic?.file instanceof File) formData.append('file', demographic.file, demographic.file.name);
  if (typeof demographic?.floatValue === 'number') formData.append('floatValue', demographic.floatValue.toString());
  if (typeof demographic?.textValue === 'string') formData.append('textValue', demographic.textValue);
  formData.append('respondent', demographic.respondent);

  demographic?.groups?.forEach((groupId: string) => formData.append('groups', groupId));

  const requestOptions = {
    method: 'POST',
    url: `/demographics/`,
    data: formData,
  };

  return api(requestOptions);
}